<script setup>
import { computed, ref, watch } from 'vue';
import { Sortable } from "sortablejs-vue3";
import { formatMoney } from '@/utils/money';
import { periodState } from '@/utils/date';
import dayjs from 'dayjs';
import axios from 'axios';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/shadcn/components/ui/dropdown-menu/index.ts";
import {
  Dialog, DialogScrollContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  ChevronDownIcon, GripVerticalIcon, PenIcon, CopyIcon, SettingsIcon,
  TicketIcon, CalendarCheckIcon, CalendarXIcon, CircleIcon, QrCodeIcon
} from "@lucide/vue";
import { Button } from "@/shadcn/components/ui/button/index.ts";
import Badge from "@/components/ui/badge/Badge.vue";
import QuotaIndicator from "@/components/QuotaIndicator.vue";
import Qrcode from "@/components/ui/Qrcode.vue";
import SegmentedControl from "@/components/ui/SegmentedControl.vue";

// Props
const props = defineProps({
  activities: {
    type: Array,
    required: true
  },
  updateOrderUrl: {
    type: String,
    required: true
  },
  locale: {
    type: String,
    required: true
  },
  timezone: {
    type: String,
    required: false
  }
});

const displayShortDate = (date) => dayjs.utc(date).tz(props.timezone).format('DD/MM/YY');

// Translation key for the activity sale state, shown as a third line.
function saleStateLabel(activity) {
  return {
    ongoing: 'Ongoing',
    upcoming: 'Upcoming',
    ended: 'Ended',
    permanent: 'Ongoing',
  }[periodState(activity.sale_start_at, activity.sale_end_at)];
}

function saleRangeClass(activity) {
  return periodState(activity.sale_start_at, activity.sale_end_at) === 'ended'
    ? 'tw:text-red-600'
    : 'tw:text-muted-foreground';
}

const items = ref(props.activities);

watch(() => props.activities, (newVal) => {
  items.value = newVal;
});

function updateOrder(event) {
  items.value.splice(event.newIndex, 0, items.value.splice(event.oldIndex, 1)[0]);
  axios.put(props.updateOrderUrl, {
    order: items.value.map((item) => item.id)
  })
    .catch(function (error) {
      alert(error);
    });
}

const csrfToken = window.Laravel.csrfToken;
const showDuplicateModal = ref(false);
const withVariants = ref(false);
const duplicateUrl = ref('');

const openDuplicateModal = (url) => {
  duplicateUrl.value = url;
  withVariants.value = false;
  showDuplicateModal.value = true;
};

// QR code of the activity public page, one per enabled locale of the tour.
const showQrcodeModal = ref(false);
const qrcodeActivity = ref(null);
const qrcodeLocale = ref('');

const hasFrontUrls = (activity) => Object.keys(activity.links?.front ?? {}).length > 0;

const qrcodeLocaleOptions = computed(() => Object.keys(qrcodeActivity.value?.links.front ?? {})
  .map((locale) => ({ value: locale, label: locale.toUpperCase() })));

const qrcodeUrl = computed(() => qrcodeActivity.value?.links.front[qrcodeLocale.value] ?? '');

const openQrcodeModal = (activity) => {
  qrcodeActivity.value = activity;
  qrcodeLocale.value = Object.keys(activity.links.front)[0];
  showQrcodeModal.value = true;
};
</script>

<template>
  <div class="table-responsive">
    <table class="table  table-sm table-striped">
      <thead>
        <tr>
          <th class="align-middle tw:text-left tw:w-10 tw:whitespace-nowrap"></th>
          <th class="align-middle tw:text-left">{{ $t('Code') }}</th>
          <th class="align-middle tw:text-left tw:w-[1%] tw:whitespace-nowrap">{{ $t('Price') }}</th>
          <th class="align-middle tw:text-left tw:w-[1%]">{{ $t('Sale period') }}</th>
          <th class="align-middle tw:text-left tw:w-[1%] tw:whitespace-nowrap">{{ $t('Quota') }}</th>
          <th class="align-middle tw:text-left tw:w-[1%] tw:whitespace-nowrap">{{ $t('Status') }}</th>
          <th class="align-middle tw:text-left tw:w-[1%] tw:whitespace-nowrap"></th>
        </tr>
      </thead>
      <Sortable
        :list="items"
        item-key="id"
        tag="tbody"
        :options="{handle:'.draggable-handle'}"
        @end="updateOrder"
      >
        <template #item="{element: activity, index}">
          <tr :key="activity.id">
            <td class="tw:px-3 tw:py-3 tw:align-middle">
              <span class="draggable-handle tw:cursor-grab tw:text-muted-foreground/50 active:tw:cursor-grabbing">
                <GripVerticalIcon class="tw:w-4 tw:h-4" />
              </span>
            </td>
            <td class="tw:px-3 tw:py-3 tw:align-middle">
              <div class="tw:flex tw:items-center tw:gap-3">
                <template v-if="activity.image">
                  <div class="tw:shrink-0 tw:w-12 tw:h-12 tw:rounded-md tw:overflow-hidden tw:border tw:border-border tw:bg-muted/20"
                  >
                    <img
                      :src="activity.image"
                      :alt="activity.name"
                      class="tw:w-full tw:h-full tw:object-cover"
                    />
                  </div>
                </template>
                <div class="tw:min-w-0">
                  <div class="tw:flex tw:items-center tw:gap-2 tw:flex-wrap">
                    <a :href="activity.links.edit"  class="tw:font-semibold tw:break-normal">
                      {{ activity.code }}
                    </a>
                    <TicketIcon v-if="activity.with_ticket" class="tw:w-3.5 tw:h-3.5 tw:text-muted-foreground" />
                  </div>
                  <div class="tw:text-xs tw:text-muted-foreground tw:mt-0.5">
                    {{ $t("variant", { count: activity.variants_count }, activity.variants_count) }}
                  </div>
                  <template v-if="activity.is_associated_activity_only">
                    <Badge variant="zinc-light" class="tw:mt-1">
                      {{ $t('Associated activity') }}
                    </Badge>
                  </template>
                </div>
              </div>
            </td>
            <td class="tw:px-3 tw:py-3 tw:align-middle">
              <template v-if="activity.price !== null">
                <div>
                  <template v-if="activity.original_price !== null">
                    <div class="tw:flex tw:items-center tw:gap-2">
                      <span class="tw:text-xs tw:text-muted-foreground tw:line-through">{{ formatMoney(activity.original_price) }}</span>
                      <span class="tw:font-semibold tw:text-green-700">{{ formatMoney(activity.price) }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <span class="tw:font-semibold tw:text-foreground">{{ formatMoney(activity.price) }}</span>
                  </template>
                </div>
              </template>
              <template v-else>
                <span class="tw:text-xs tw:italic tw:text-destructive">{{ $t('No price') }}</span>
              </template>
              <div v-if="activity.tax_rate" class="tw:text-xs tw:text-muted-foreground tw:mt-0.5">
                {{ $t('Tax rate') }} {{ activity.tax_rate.amount.toFixed(2) }}%
              </div>
            </td>
            <td class="tw:px-3 tw:py-3 tw:align-middle tw:whitespace-nowrap">
              <div
                class="tw:space-y-0.5 tw:text-xs tw:tabular-nums"
                :class="saleRangeClass(activity)"
              >
                <div class="tw:flex tw:items-center tw:gap-1.5">
                  <CalendarCheckIcon class="tw:w-3 tw:h-3 tw:shrink-0" />
                  {{ activity.sale_start_at ? displayShortDate(activity.sale_start_at) : '-' }}
                </div>
                <div class="tw:flex tw:items-center tw:gap-1.5">
                  <CalendarXIcon class="tw:w-3 tw:h-3 tw:shrink-0" />
                  {{ activity.sale_end_at ? displayShortDate(activity.sale_end_at) : '-' }}
                </div>
                <div class="tw:font-medium">{{ $t(saleStateLabel(activity)) }}</div>
              </div>
            </td>

            <!-- Quota -->
            <td class="tw:px-3 tw:py-3 tw:align-middle tw:min-w-[90px]">
              <QuotaIndicator :quota="activity.quota" size="sm" />
            </td>

            <!-- Status -->
            <td class="tw:px-3 tw:py-3 tw:align-middle tw:text-center">
              <Badge :variant="activity.is_active ? 'enabled' : 'disabled'">
                <CircleIcon class="tw:w-2 tw:h-2 tw:fill-current" />
                {{ activity.is_active ? $t('Active') : $t('Inactive') }}
              </Badge>
            </td>
            <td class="align-middle">
              <DropdownMenu>
                <DropdownMenuTrigger as-child>
                  <Button variant="outline">
                    {{ $t('Actions') }}
                    <ChevronDownIcon class="tw:ml-2 tw:h-4 tw:w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem as-child>
                    <a :href="activity.links.edit">
                      <PenIcon class="tw:w-4 tw:h-4" />
                      {{ $t('Edit') }}
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem as-child>
                    <a :href="activity.links.show">
                      <SettingsIcon class="tw:w-4 tw:h-4" />
                      {{ $t('Manage variants') }}
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem as-child>
                    <button @click="openDuplicateModal(activity.links.duplicate)">
                      <CopyIcon class="tw:w-4 tw:h-4" />
                      {{ $t('Duplicate') }}
                    </button>
                  </DropdownMenuItem>
                  <DropdownMenuItem v-if="hasFrontUrls(activity)" as-child>
                    <button @click="openQrcodeModal(activity)">
                      <QrCodeIcon class="tw:w-4 tw:h-4" />
                      {{ $t('QR code') }}
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          </tr>
        </template>
      </Sortable>
    </table>

    <Dialog :open="showDuplicateModal" @update:open="showDuplicateModal = $event">
      <DialogScrollContent>
        <form :action="duplicateUrl" method="POST">
          <input type="hidden" name="_token" :value="csrfToken" />
          <DialogHeader>
            <DialogTitle>{{ $t('Duplicate') }}</DialogTitle>
            <DialogDescription>
              {{ $t('A new activity will be created with the same settings as the original.') }}
            </DialogDescription>
          </DialogHeader>
          <div class="tw:py-4">
            <div class="tw:flex tw:items-center tw:gap-2">
              <input id="with-variants" v-model="withVariants" type="checkbox" name="with_variants" value="1" />
              <label for="with-variants" class="tw:cursor-pointer">
                {{ $t('Duplicate with variants') }}
              </label>
            </div>
            <p class="tw:text-sm tw:text-muted-foreground tw:mt-1 tw:ml-6">
              {{ $t('If checked, all variants and their options will also be duplicated.') }}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" @click="showDuplicateModal = false">
              {{ $t('Cancel') }}
            </Button>
            <Button type="submit">
              {{ $t('Duplicate') }}
            </Button>
          </DialogFooter>
        </form>
      </DialogScrollContent>
    </Dialog>

    <Dialog :open="showQrcodeModal" @update:open="showQrcodeModal = $event">
      <DialogScrollContent size="sm">
        <DialogHeader>
          <DialogTitle>{{ $t('QR code') }}</DialogTitle>
          <DialogDescription>
            {{ $t('Leads to the activity page on the event website.') }}
          </DialogDescription>
        </DialogHeader>
        <div v-if="qrcodeActivity" class="tw:flex tw:flex-col tw:gap-4 tw:py-4">
          <SegmentedControl
            v-if="qrcodeLocaleOptions.length > 1"
            v-model="qrcodeLocale"
            :options="qrcodeLocaleOptions"
            fullwidth
          />
          <Qrcode
            :value="qrcodeUrl"
            :filename="`${qrcodeActivity.code}-${qrcodeLocale}.png`"
            with-download
          />
        </div>
      </DialogScrollContent>
    </Dialog>
  </div>
</template>
